// /logga-in — where a mailed login link lands
// (docs/plan/2026-09-11-magic-link-login.md).
//
// Reached two ways: a real click from an email (?token=… in the query
// string, the app verifies it and signs the reader in), or a reader who
// lands here with nothing to verify — a bookmark, a stale link with the
// token already stripped, or just typing the address — in which case it is
// simply the "request a login link" form, same as the top bar's popover.
//
// Not part of the sitemap and carries noindex/nofollow (see
// getRapportvaktLoginSeo in src/config/seoRoutes.js): nothing here should
// ever be a search result, only a destination a reader is sent to by mail.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RapportvaktShell, { useRvAccount, useRvLang } from '../components/rapportvakt/Shell';
import { verifyLoginLink } from '../components/rapportvakt/api';
import { saveSession } from '../components/rapportvakt/storage';
import { rapportvaktTabPath } from '../config/rapportvakt';
import { getRapportvaktLoginSeo } from '../config/seoRoutes';
import { track } from '../lib/analytics';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The "request a login link" form. Shown from the very first render when
 * there is no token to verify, and again if verifying one fails — `error`
 * is the one-line reason printed above the form in that second case.
 */
function LoginForm({ error }) {
  const { t } = useRvLang();
  const { requestLink } = useRvAccount();
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [pending, setPending] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    if (pending) return;
    const value = email.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setFieldError(t.loginError);
      return;
    }
    setFieldError('');
    setPending(true);
    const result = await requestLink(value);
    setPending(false);
    if (!result.ok) {
      setFieldError(result.message || t.offlineNote);
      return;
    }
    track('login_link_sent');
    setSentEmail(value);
  };

  if (sentEmail) {
    return <p className="rv-pickform__hint">{t.loginSent(sentEmail)}</p>;
  }

  return (
    <>
      {error ? <p className="rv-pickform__error">{error}</p> : null}
      <form className="rv-pickform" onSubmit={submit}>
        <div className="rv-pickform__row">
          <input
            className="rv-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t.loginPlaceholder}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setFieldError('');
            }}
            aria-label={t.loginEmailLabel}
            disabled={pending}
          />
          <button type="submit" className="rv-btn rv-btn--primary" disabled={pending}>
            {pending ? `${t.sending}…` : t.loginBtn}
          </button>
        </div>
        {fieldError ? <p className="rv-pickform__error">{fieldError}</p> : null}
      </form>
      <p className="rv-pickform__hint">{t.loginHint}</p>
    </>
  );
}

function LoginBody() {
  const { t } = useRvLang();
  const { refresh } = useRvAccount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // null while a token is still being verified; '' once there is nothing to
  // verify (no token at all, so the form shows right away); a string is the
  // refusal to show above the form after a failed verify.
  const [error, setError] = useState(token ? null : '');

  // A login link works exactly once, so this effect must fire exactly once
  // per token. React 18's development StrictMode mounts, unmounts and
  // remounts every component, which re-ran the effect and spent the token
  // twice; the ref remembers the first run across that remount, and no
  // cancelled flag is needed because the first run's result is the one we
  // want to keep.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!token || startedRef.current) return undefined;
    startedRef.current = true;
    verifyLoginLink(token).then((result) => {
      if (!result.ok) {
        setError(result.offline ? t.offlineNote : t.loginLinkFailed);
        return;
      }
      // Save first, refresh second: refresh() reads the session storage.js
      // just wrote to decide whether it can call /auth/me.
      saveSession({ token: result.token, email: result.email, tier: result.tier, limit: result.limit });
      refresh();
      navigate(rapportvaktTabPath('digest'), { replace: true });
    });
    return undefined;
    // Only the token this page mounted with is ever verified — a changed
    // `t`/`refresh` identity must not re-trigger a second verify call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <main className="rv__inner">
      <div className="rv-login">
        <h1 className="rv-login__title">{t.loginBtn}</h1>
        {error === null ? <p className="rv-login__status">{t.loginVerifying}</p> : <LoginForm error={error || null} />}
      </div>
    </main>
  );
}

export default function RapportvaktLoginPage() {
  return (
    <RapportvaktShell seo={getRapportvaktLoginSeo()}>
      <LoginBody />
    </RapportvaktShell>
  );
}
